import {
  ACHIEVEMENTS,
  ATTR_ICON,
  ATTR_NAME,
  BRANCH_NODES,
  DEATH_TYPES,
  ENDINGS,
  FATE_LINES,
  HOTSPOTS,
  LIFE_TITLES,
  NODES,
  SCENE_PALETTE
} from '@sun-ge/content'
import canonicalContent from '@sun-ge/content'
import homeScene from './assets/scenes/reference-scene-home.svg'
import schoolScene from './assets/scenes/reference-scene-school.svg'
import cryptoScene from './assets/scenes/reference-scene-crypto.svg'
import officeScene from './assets/scenes/reference-scene-office.svg'
import cityScene from './assets/scenes/reference-scene-city.svg'
import borderScene from './assets/scenes/reference-scene-border.svg'
import artScene from './assets/scenes/reference-scene-art.svg'
import lawScene from './assets/scenes/reference-scene-law.svg'
import powerScene from './assets/scenes/reference-scene-power.svg'

import homePortrait from './assets/scenes/reference-scene-home.svg'
import schoolPortrait from './assets/scenes/reference-scene-school.svg'
import cryptoPortrait from './assets/scenes/reference-scene-crypto.svg'
import officePortrait from './assets/scenes/reference-scene-office.svg'
import cityPortrait from './assets/scenes/reference-scene-city.svg'
import borderPortrait from './assets/scenes/reference-scene-border.svg'
import artPortrait from './assets/scenes/reference-scene-art.svg'
import lawPortrait from './assets/scenes/reference-scene-law.svg'
import powerPortrait from './assets/scenes/reference-scene-power.svg'

export const scenePortraits: Readonly<Record<string, string>> = {
  [homeScene]: homePortrait,
  [schoolScene]: schoolPortrait,
  [cryptoScene]: cryptoPortrait,
  [officeScene]: officePortrait,
  [cityScene]: cityPortrait,
  [borderScene]: borderPortrait,
  [artScene]: artPortrait,
  [lawScene]: lawPortrait,
  [powerScene]: powerPortrait,
}

export type AttributeKey = 'asset' | 'intel' | 'luck' | 'face' | 'ability' | 'fame' | 'feel'
export type DeltaKey = AttributeKey | 'black' | 'risk' | 'rep' | 'clean'
export type Delta = Partial<Record<DeltaKey, number>>

export interface Choice {
  text: string
  tag?: string
  sec?: number
  fx?: Delta
  result?: string
  flag?: string
  deadIf?: string
  dead?: { type: string; text: string }
  goto?: string
  egg?: string
}

export interface StoryNode {
  histAns?: number | Record<number, number>
  histBadge?: string
  id: string
  title: string
  scene?: string
  sc?: string
  tag?: string
  age?: string
  year?: string
  special?: string
  sections?: { label: string; desc: string }[]
  choices: Choice[]
}

export interface FateLine {
  id: string
  name: string
  icon: string
  cp?: string
  story?: string
  knock?: string
  high: { name: string; text: string }
  cost: { name: string; text: string }
  nodes?: string[]
  cond?: (attrs: Record<string, number>) => boolean
}

export interface BranchNode extends StoryNode {
  line: string
}

export interface Ending {
  id: string
  icon: string
  name: string
  desc: string
  cond?: (state: Record<string, number>) => boolean
}

export interface DeathType {
  icon: string
  name: string
  title: string
}

export interface FakeAd {
  title: string
  text: string
}

export interface StoryContent {
  nodes: StoryNode[]
  fateLines: FateLine[]
  branchNodes: Record<string, BranchNode>
  endings: Ending[]
  deathTypes: Record<string, DeathType>
  achievements: { id: string; icon: string; name: string; desc: string }[]
  lifeTitles: { id: string; icon: string; name: string; desc: string }[]
  hotspots: { text: string; fx: Delta }[]
  fakeAds: ReadonlyArray<FakeAd>
  /** Safe image sources. Consumers can assign these to an <img src> without injecting SVG markup. */
  scenes: Readonly<Record<string, string>>
  scenePalette: Record<string, { bg: string; border: string; dot: string; tagBg: string; tagInk: string }>
  attrNames: Record<AttributeKey, string>
  attrIcons: Record<AttributeKey, string>
}

/** Hand-drawn chapter SVGs, with a quiet palette and clear reading area. */
export const sceneAssets: Readonly<Record<string, string>> = Object.freeze({
  home: homeScene,
  school: schoolScene,
  crypto: cryptoScene,
  office: officeScene,
  city: cityScene,
  border: borderScene,
  art: artScene,
  law: lawScene,
  power: powerScene
})

export const fakeAds: ReadonlyArray<FakeAd> = Object.freeze(
  (canonicalContent.FAKE_ADS as FakeAd[]).map(ad => Object.freeze({ title: ad.title, text: ad.text }))
)

/**
 * The modern app reads the canonical ESM content package. This adapter is
 * deliberately data-only: React owns presentation and the game state machine,
 * while story text and choices remain in @sun-ge/content.
 */
export const content: StoryContent = {
  nodes: NODES as StoryNode[],
  fateLines: FATE_LINES as FateLine[],
  branchNodes: BRANCH_NODES as Record<string, BranchNode>,
  endings: ENDINGS as Ending[],
  deathTypes: DEATH_TYPES as Record<string, DeathType>,
  achievements: ACHIEVEMENTS as StoryContent['achievements'],
  lifeTitles: LIFE_TITLES as StoryContent['lifeTitles'],
  hotspots: HOTSPOTS as StoryContent['hotspots'],
  fakeAds,
  scenes: sceneAssets,
  scenePalette: SCENE_PALETTE as StoryContent['scenePalette'],
  attrNames: ATTR_NAME as Record<AttributeKey, string>,
  attrIcons: ATTR_ICON as Record<AttributeKey, string>
}

export const attributeKeys: AttributeKey[] = ['asset', 'intel', 'luck', 'face', 'ability', 'fame', 'feel']

export function getMainNode(stateNodeIdx: number): StoryNode | null {
  return content.nodes[stateNodeIdx] || null
}

export function getBranchNode(id: string | null | undefined): BranchNode | null {
  return id ? content.branchNodes[id] || null : null
}

export function getFateLine(id: string | null | undefined): FateLine | null {
  return id ? content.fateLines.find(line => line.id === id) || null : null
}

export function getSceneAsset(id: string | null | undefined): string {
  return (id && content.scenes[id]) || content.scenes.home || ''
}

export function formatDelta(delta: Delta | null | undefined): string {
  if (!delta) return ''
  const deltaKeys: DeltaKey[] = [...attributeKeys, 'black', 'risk', 'rep', 'clean']
  return deltaKeys.reduce<string[]>((items, key) => {
    const value = delta[key]
    if (typeof value === 'number' && value !== 0) {
      const label = content.attrNames[key as AttributeKey] || ({ black: '黑料', risk: '风险', rep: '声望', clean: '清白' } as Record<string, string>)[key]
      items.push(`${label} ${value > 0 ? '+' : ''}${value}`)
    }
    return items
  }, []).join(' · ')
}
